import { Icon } from '@drdropin-tech/theseus';
import { useIsChooseDoctorFlow } from '@hooks/useIsChooseDoctorFlow';
import { useNavigationStore } from '@packages/shared';
import LocaleSwitcher from '@packages/ui-web/src/components/LocaleSwitcher';
import classNames from 'classnames';
import { stepCount } from 'layouts/BookingSteps';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { shallow } from 'zustand/shallow';

export default function BookingNav() {
  const { t } = useTranslation(['booking']);
  const router = useRouter();

  const isChooseGpPractitionerService = useIsChooseDoctorFlow();
  const { queries, getPreviousStep, currStep, goToStep, setQueries } =
    useNavigationStore(
      (state) => ({
        queries: state.queries,
        getPreviousStep: state.getPreviousStep,
        currStep: state.currStep,
        goToStep: state.goToStep,
        setQueries: state.setQueries,
      }),
      shallow,
    );

  const locales: Array<{ locale: string; labels: { nb: string; en: string } }> =
    [
      {
        locale: 'nb',
        labels: {
          nb: 'Norsk',
          en: 'Norwegian',
        },
      },
      {
        locale: 'en',
        labels: {
          nb: 'Engelsk',
          en: 'English',
        },
      },
    ];

  const partner = queries.partner;
  const isStorebrand = partner === 'storebrand';
  const isNorwegian = partner === 'norwegian';
  // norwegian campaign skips first step
  const isNotFirstStep = isNorwegian ? currStep > 1 : currStep > 0;
  const isNotLastStep = currStep < stepCount - 1;

  const canGoBack = isNotFirstStep && isNotLastStep;

  const handleBack = () => {
    if (currStep === 0) {
      router.back();
    } else if (currStep === 4) {
      if (isChooseGpPractitionerService) {
        goToStep(2);
        getPreviousStep();
      } else {
        getPreviousStep();
      }
    } else if (currStep === 5) {
      // select date and time step
      if (
        queries.vertical === 'mental-health' ||
        isChooseGpPractitionerService
      ) {
        if (isChooseGpPractitionerService) {
          setQueries({ clinicId: null });
        }
        getPreviousStep();
      } else if (queries.practitionerId) {
        setQueries({ practitionerId: null });
      } else {
        goToStep(4); // step 4 is skipped by all verticals except for mental-health, therefore we go to this step and then go back another step to end up on step 3.
        getPreviousStep(); // Use getPreviousStep to clear query params (this is not handled by goToStep, hence we can't just goToStep(3) directly)
      }
    } else if (currStep === 6) {
      // personal details step
      if (
        queries.vertical !== 'mental-health' &&
        !isChooseGpPractitionerService
      ) {
        setQueries({ practitionerId: null });
      }
      getPreviousStep();
    } else {
      getPreviousStep();
    }
  };

  const progress = ((currStep + 1) * 100) / stepCount;

  return (
    <nav className="t-0 bg-primary sticky flex w-full flex-col items-center justify-center">
      <div className="flex w-full max-w-3xl items-center justify-between px-9 py-10 md:py-12">
        <button
          onClick={handleBack}
          type="button"
          className={classNames({ invisible: !canGoBack }, 'z-10')}
          data-testid="nav-back-button"
        >
          <Icon name="ArrowLeft" />
        </button>

        <div className="absolute flex w-[calc(100%-72px)] max-w-[calc(48rem-72px)] justify-center">
          <div className="w-25 rounded-lg bg-black bg-opacity-30 md:w-60">
            <div
              className="bg-secondary h-2 rounded-lg transition-all"
              style={{
                width: `${progress}%`,
              }}
            />
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-4">
          {locales.length > 1 && (
            <LocaleSwitcher locales={locales} translation={t} />
          )}
          <a
            href={getCloseLink}
            className={classNames({
              'pointer-events-none invisible': isStorebrand,
            })}
          >
            <Icon className="block" name="X" />
          </a>
        </div>
      </div>
    </nav>
  );
}

const getCloseLink =
  process.env.NODE_ENV === 'production' ? 'https://www.drdropin.no/' : '/';
